import { useEffect, useReducer } from 'react';

import type { RentalManagerAuthCheckUserInfoWebApiDto } from '@zg-rentals/ts-rental-manager-api-beta-client';
import type { UserProps } from '../../../../../srcLegacy/shared/model/User';

type UserProfileProps = { firstName: string; lastName: string; propertyCount: string };
type ErrorProps = { firstName: boolean; lastName: boolean; propertyCount: boolean };

export const useValidateUserProfile = (
  { firstName, lastName, propertyCount }: UserProfileProps,
  setIsSubmitDisabled: (arg: boolean) => void,
  user: RentalManagerAuthCheckUserInfoWebApiDto | UserProps | null,
) => {
  const { firstName: existingFirstName = '', lastName: existingLastName = '' } = user || {};

  const [userProfileErrors, setUserProfileErrors] = useReducer(
    (state: ErrorProps, { type, payload }: { type: string; payload: boolean }) => {
      return { ...state, [type]: payload };
    },
    { firstName: false, lastName: false, propertyCount: false },
  );

  useEffect(() => {
    const didClearFirstName = existingFirstName && !firstName;
    if (didClearFirstName) {
      setUserProfileErrors({ type: 'firstName', payload: true });
      setIsSubmitDisabled(true);
    } else if (userProfileErrors.firstName && firstName) {
      setUserProfileErrors({ type: 'firstName', payload: false });
      setIsSubmitDisabled(false);
    }
  }, [firstName, existingFirstName, userProfileErrors.firstName, setIsSubmitDisabled]);

  useEffect(() => {
    const didClearLastName = existingLastName && !lastName;
    if (didClearLastName) {
      setUserProfileErrors({ type: 'lastName', payload: true });
      setIsSubmitDisabled(true);
    } else if (userProfileErrors.lastName && lastName) {
      setUserProfileErrors({ type: 'lastName', payload: false });
      setIsSubmitDisabled(false);
    }
  }, [lastName, existingLastName, userProfileErrors.lastName, setIsSubmitDisabled]);

  useEffect(() => {
    if (
      isNaN(Number(propertyCount)) ||
      propertyCount.includes('.') ||
      Number(propertyCount) < 0 ||
      Number(propertyCount) > 1000
    ) {
      setUserProfileErrors({ type: 'propertyCount', payload: true });
      setIsSubmitDisabled(true);
    } else if (userProfileErrors.propertyCount) {
      setUserProfileErrors({ type: 'propertyCount', payload: false });
      setIsSubmitDisabled(false);
    }
  }, [propertyCount, userProfileErrors.propertyCount, setIsSubmitDisabled]);

  return { userProfileErrors };
};
